import React, { useState, useCallback, useMemo } from 'react';
import RadioButton from '../../../../../../../sequence/components/sequence-single-content/components/sequence-single-settings/components/sequence-single-settings-content/components/sequence-unsubscribe-option/components/radio-button';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay/tooltip/overlay-tooltip';
import { Placement } from '../../../../../../../../shared/design-system/components/overlay-taget/enums';
import PrimaryValueChangeAlertModal from './primary-value-change-alert-modal';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import { getEmailVerificationIcon } from '../../../../../../../../shared/functions/get-email-verification-icon';
import Icon from '../../../../../../../../shared/design-system/components/atoms/icon/icon';
import { OptionItem, PrimaryValueChangeDropdownProps } from './types';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';

const PrimaryValueChangeDropdown: React.FC<PrimaryValueChangeDropdownProps> = ({
  options = [],
  tooltipText = 'Set as Primary',
  modalTitle = 'Change Primary Value',
  modalContent = 'Are you sure you want to change the primary value?',
  initialSelectedValue = '',
  changePrimaryEmailsInProspect,
  updatePrimaryNumbers,
  contactId,
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(
    initialSelectedValue,
  );
  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);

  // Get the selected option
  const selectedOption = useMemo(
    () => options.find((o) => o.key === selectedValue),
    [options, selectedValue],
  );

  const handleSelectedValue = useCallback((option: OptionItem) => {
    if (option?.key === selectedValue) return;
    setSelectedValue(option?.key);
    setShowAlertModal(true);
  }, []);

  const handleAlertModalClose = useCallback(() => {
    setShowAlertModal(false);
  }, []);

  const handleAlertModalConfirm = useCallback(() => {
    if (!selectedOption) return;

    if (selectedOption.isEmail) {
      changePrimaryEmailsInProspect(contactId, Number(selectedOption.key));
    } else {
      updatePrimaryNumbers(contactId, Number(selectedOption.key));
    }

    setShowAlertModal(false);
  }, [
    selectedOption,
    contactId,
    changePrimaryEmailsInProspect,
    updatePrimaryNumbers,
  ]);

  const getVerificationIcon = (emailVerificationStatus?: string) => {
    const iconConfig = getEmailVerificationIcon(
      emailVerificationStatus || 'skip',
    );
    return (
      <Icon
        identifier={iconConfig.identifier}
        className={iconConfig.className}
      />
    );
  };

  return (
    <>
      <div
        className={`${
          showAlertModal
            ? 'primary-value-change-dropdown-hidden'
            : 'primary-value-change-dropdown'
        }`}
      >
        {options?.map((option) => (
          <div
            key={option.key}
            className="primary-value-change-dropdown-value"
            {...accessibleOnClick(() => handleSelectedValue(option))}
          >
            <OverlayTooltip
              text={tooltipText}
              placement={Placement.Bottom}
              className={
                selectedValue === option.key || showAlertModal
                  ? 'primary-value-change-dropdown-tooltip'
                  : ''
              }
            >
              <div className="primary-value-radio-btn">
                <RadioButton
                  name="primary-value-change"
                  value={option.value}
                  label={option.label}
                  checked={selectedValue === option.key}
                  disabled={false}
                  onChange={() => {}}
                />
              </div>
            </OverlayTooltip>
            <div className="d-flex align-items-center gap-4">
              {option.isEmail && (
                <OverlayTooltip
                  text={
                    getEmailVerificationIcon(
                      option.emailVerificationStatus || 'skip',
                    ).toolTipText
                  }
                  placement={Placement.Bottom}
                >
                  {getVerificationIcon(option.emailVerificationStatus)}
                </OverlayTooltip>
              )}
              <span className="primary-value-change-dropdown-value-text d-flex align-items-center">
                <TruncatedTextWithAutoTooltip text={option.value} />
              </span>
            </div>
          </div>
        ))}
      </div>

      <PrimaryValueChangeAlertModal
        show={showAlertModal}
        onHide={handleAlertModalClose}
        onConfirm={handleAlertModalConfirm}
        modalTitle={modalTitle}
        modalContent={modalContent}
      />
    </>
  );
};

export default PrimaryValueChangeDropdown;
